import React from 'react';
import { PanelTable } from '../../components/display/PanelTable/PanelTable';
import { SectionCard } from '../../components/sections/SectionCard';
import { generatePlanPricingData, type PricingItem, type PricingPhase } from '@shared/data/mockSubscriptionPlanDetails';
import type { PanelTableColumn, ExpandableConfig } from '../../components/display/PanelTable/types';
import type { Plan } from '@/features/subscriptions/types/plans/plan.types';

interface PricingSectionProps {
  isExpanded?: boolean;
  onToggle?: () => void;
  isDetailView?: boolean;
  planData?: Plan;
  className?: string;
}

const phaseColumns: PanelTableColumn<PricingPhase>[] = [
  {
    key: 'name',
    label: 'NAME',
    headerClassName: 'w-64',
    cellClassName: 'font-medium text-sm text-foreground',
  },
  {
    key: 'period',
    label: 'PERIOD',
    cellClassName: 'text-sm text-foreground',
  }
];

const itemColumns: PanelTableColumn<PricingItem>[] = [
  {
    key: 'name',
    label: 'ITEM',
    headerClassName: 'w-[300px]',
    render: (_, item) => (
      <div className="flex flex-col gap-0.5">
        <div className="font-medium text-sm text-foreground leading-none">
          {item.name}
        </div>
        <div className="text-xs font-normal text-muted-foreground">
          {item.type}
        </div>
      </div>
    ),
  },
  {
    key: 'repeats',
    label: 'REPEATS',
    headerClassName: 'w-[150px]',
    cellClassName: 'text-sm text-foreground',
  },
  {
    key: 'billed',
    label: 'BILLED',
    headerClassName: 'w-[150px]',
    cellClassName: 'text-sm text-foreground',
  },
  {
    key: 'discount',
    label: 'DISCOUNT',
    headerClassName: 'w-[120px]',
    cellClassName: 'text-sm text-foreground',
  },
  {
    key: 'amount',
    label: 'AMOUNT',
    headerClassName: 'w-[140px]',
    cellClassName: 'font-semibold text-sm text-foreground',
  }
];

// Row actions for phases
const phaseRowActions = (phase: PricingPhase) => [
  {
    key: 'update-phase',
    label: 'Update phase',
    onClick: () => console.log('Update phase', phase)
  },
  {
    key: 'remove-phase',
    label: 'Remove phase',
    onClick: () => console.log('Remove phase', phase),
    variant: 'destructive' as const
  }
];

// Row actions for items
const itemRowActions = (item: PricingItem) => [
  {
    key: 'view-item',
    label: 'View item',
    onClick: () => console.log('View item', item)
  },
  {
    key: 'update-item',
    label: 'Update item',
    onClick: () => console.log('Update item', item)
  },
  {
    key: 'remove-item',
    label: 'Remove item',
    onClick: () => console.log('Remove item', item),
    variant: 'destructive' as const
  }
];

const expandableConfig: ExpandableConfig<PricingPhase, PricingItem> = {
  expandedColumns: itemColumns,
  getExpandedData: (phase) => phase.items,
  showExpandIcon: true,
  expandedRowActions: itemRowActions,
};

export const PricingSection = React.memo<PricingSectionProps>(({
  isExpanded = true,
  onToggle,
  isDetailView = false,
  planData,
  className,
}) => {
  // Use real data if available, otherwise fall back to mock data
  const pricingData = planData
    ? generatePlanPricingData(planData)
    : generatePlanPricingData();

  return (
    <SectionCard
      title="Pricing"
      isCollapsible={!isDetailView}
      isCollapsed={!isExpanded}
      onTitleClick={onToggle}
      className={className}
    >
      <PanelTable<PricingPhase, PricingItem>
        data={pricingData}
        columns={phaseColumns}
        variant="expandable"
        expandableConfig={expandableConfig}
        showPagination={false}
        rowActions={phaseRowActions}
      />
    </SectionCard>
  );
});

PricingSection.displayName = 'PricingSection';
